'use client';

import { SetStateAction } from 'react';
import { useRouter } from 'next/navigation';
import { UseQueryResult } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { CONSTANT } from '@const';
import { Time } from '@internationalized/date';
import { NotificationLinkType, Page, PageDqp, PageDqpFilter, PageFilter } from '@types';
import { isNil } from 'lodash';
import * as ReactDeviceDetect from 'react-device-detect';

import { msalConfig } from '../authConfig';

const IS_LOGGING_OUT_KEY = 'isLoggingOut';

/**
 * Trusted domains that the application is permitted to redirect to.
 * Hardcoded to prevent misconfiguration via deployment — changes require a code review.
 * Used to validate redirect URLs and prevent open redirect attacks.
 * @see https://owasp.org/www-project-web-security-testing-guide/latest/4-Web_Application_Security_Testing/11-Client-side_Testing/04-Testing_for_Client-side_URL_Redirect
 */
const TRUSTED_LOGOUT_REDIRECT_DOMAINS: string[] = [
	'.water.qld.gov.au',
	'.auth.qld.gov.au',
	...(process.env.NODE_ENV === 'development' ? ['localhost'] : []),
];

/**
 * Validates whether a URL is safe to redirect to by checking its hostname
 * against the allowlist of trusted domains.
 * @param {string} url - The URL to validate
 * @returns {boolean} true if the URL is within a trusted domain
 */
export const isValidRedirectUrl = (url: string): boolean => {
	try {
		const parsed = new URL(url);
		const hostname = parsed.hostname.toLowerCase();

		return TRUSTED_LOGOUT_REDIRECT_DOMAINS.some(domain => {
			if (domain.startsWith('.')) {
				// Subdomain match: hostname must end with the domain or equal it without the leading dot
				return hostname.endsWith(domain) || hostname === domain.slice(1);
			}
			return hostname === domain;
		});
	} catch {
		return false;
	}
};

/**
 * Returns an empty id value if it is undefined. Otherwise, original id value is returned
 * @param {string | undefined} id unkown id value
 */
export const safeId = (id: string | undefined | null) => {
	return id ? id : '';
};

/**
 * Finds an item in a list by its id.
 * @template T - The type of the items in the list, which must include an 'id' property.
 * @param {T[]} [items=[]] - The list of items to search through.
 * @param {string} itemId - The id of the item to find.
 * @returns {T | undefined} The item with the matching id, or undefined if not found.
 */
export const findItemById = <T extends { id: string }>(items: T[] = [], itemId: string) => {
	return items.find(item => item.id === itemId);
};

/**
 * Validates if a string is not empty and not the invalid UUID constant
 * @param {string | undefined | null} uuid string to validate
 * @returns {boolean} true if uuid is valid, false otherwise
 */
export const isValidUUID = (uuid: string | undefined | null): boolean => {
	return !!uuid && !!uuid.trim() && uuid !== CONSTANT.INVALID_UUID;
};

/**
 * Format string to sentence case
 * @param {string} str : target
 * @returns {object} return strings in sentence case
 */
export const toSentenceCase = (str: string) => {
	if (typeof str !== 'string' || !str) return '';

	if (str.length > 1) {
		return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
	} else {
		return str.toUpperCase();
	}
};

/**
 * Join nullable values including separator
 * @param values nullable values to get joined
 * @param separator symbol to add between values
 * @returns {string} values joined with separator
 */
export const joinNonNullValues = (values: Array<string | undefined>, separator = ', ') => {
	if (!values) {
		return '';
	}
	return values.filter(value => value != null).join(separator);
};

/**
 * format number for display
 * @param value string that represent number
 * @param decimals number of decimals to format
 * @returns {string} formatted number
 */
export const formatNumber = (value: string, decimals = 2) => {
	if (isNil(value) || isNaN(parseFloat(value))) return '';

	return parseFloat(value).toFixed(decimals);
};

/**
 * Removes duplicate items from list checking duplicity by item's id
 * @param {T[]} items
 * @returns items list without duplicates
 */
export const removeDuplicates = <T extends { id: string }>(items: T[]) => {
	return items.reduce(
		(accumulator: T[], item: T) =>
			accumulator.find(x => item.id == x.id) ? accumulator : [...accumulator, item],
		[],
	);
};

/**
 * custom hook to check whether there is an ongoing logout process or not
 * @returns {boolean}
 */
export const useIsLoggingOut = () => {
	if (typeof window === 'undefined') return false;
	return Boolean(localStorage.getItem(IS_LOGGING_OUT_KEY));
};

/**
 * custom hook which returns logout function
 * navigates to logout page and do logout after a delay
 * @param {string} [message] additional message to display
 * @param {number} [delay=1000] timeout to close user's session
 * @returns {Function} the function that dictates how logout works
 */
export const useLogout = (message?: string, delay = 1000) => {
	const { instance } = useMsal();
	const router = useRouter();
	const logout = () => {
		if (instance) {
			localStorage.setItem(IS_LOGGING_OUT_KEY, 'is logging out');

			const constructedRedirectUri = `${msalConfig.auth.postLogoutRedirectUri}${msalConfig.auth.redirectUri}`;
			const postLogoutRedirectUri = isValidRedirectUrl(constructedRedirectUri)
				? constructedRedirectUri
				: `${msalConfig.auth.redirectUri}`;

			if (document.visibilityState === 'visible') {
				// only redirect to the internal log-out page if the browser tab is active - otherwise the post Logout redirection doesn't fire,
				// the user TUO session ins properly ended and attempts to log in again in with the same browser session fail
				if (message) {
					router.push(`/logout?message=${encodeURIComponent(message)}`);
				} else {
					router.push('/logout');
				}
				setTimeout(() => {
					localStorage.clear(); // Clear logout flag
					sessionStorage.clear(); // Clear selected client and user type
					instance?.logoutRedirect({ postLogoutRedirectUri });
				}, delay);
			} else {
				localStorage.clear(); // Clear logout flag
				sessionStorage.clear(); // Clear selected client and user type
				instance?.logoutRedirect({ postLogoutRedirectUri });
			}
		}
	};

	return logout;
};

/**
 * custom hook which returns auto logout function
 * @returns {Function} the function that invokes logout after a delay
 */
export const useAutoLogout = () => {
	const logout = useLogout();
	const autoLogout = () => {
		setTimeout(() => {
			logout();
		}, CONSTANT.AUTO_LOGOUT_TIMEOUT);
	};
	return autoLogout;
};

/**
 * Convert file to an bytes array.
 * File is previously parsed as an array of 8-bit unsigned integers (Uint8Array).
 * @param {File} file
 * @returns {number[]} bytes array
 */
export const convertFileToBytes = async (file: File) => {
	const arrayBuffer = await file.arrayBuffer();
	const uint8Array = new Uint8Array(arrayBuffer);
	const bytesArray: number[] = Array.from(uint8Array);
	return bytesArray;
};

/**
 * Validates wheter a file does not exceed a give file size
 * @param {File} file
 * @param {number} maxFileMBSize maximun file size in Megabytes
 * @returns {string} error message with current file size
 */
export const checkFileSize = (file: File, maxSize: number): string | null => {
	if (!maxSize) {
		return null;
	}

	const maxBytesSize = maxSize * 1024 * 1024;
	if (maxSize && file.size > maxBytesSize) {
		const currentSize = formatBytes(file.size);
		return `File size (${currentSize}) is greater than ${maxSize} MB`;
	}

	return null;
};

/**
 * Converts number of bytes into a readable file size unit
 * Example: 10485760 -> '10 MB'
 * @param bytes
 * @param decimals
 * @returns {string} file size in readable unit
 */
export const formatBytes = (bytes: number, decimals = 2) => {
	if (!+bytes) return '0 Bytes';

	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

/**
 * Sanitizes a filename by replacing non-ASCII or disallowed characters with underscores.
 * Preserves: A-Z, a-z, 0-9, and safe punctuation !#$%&'().-=@[]^_{}~ and space
 * @param {string} filename - The filename to sanitize
 * @returns {string} The sanitized filename with only allowed ASCII characters
 */
export const sanitizeFilename = (filename: string): string => {
	if (!filename) return 'file';

	// 1. Replace anything NOT in our allowed ASCII whitelist with "_"
	const whitelistRegex = /[^a-zA-Z0-9!#$%&'().\-=@[\]^_{}~ ]/g;
	let clean = filename.replace(whitelistRegex, '_');

	// 2. Windows specific: Filenames cannot end with a dot or a space
	clean = clean.replace(/[ .]$/, '_');

	// 3. Limit length to 255 (standard filesystem limit)
	if (clean.length > 255) {
		clean = clean.substring(0, 255);
	}

	return clean;
};

/**
 * Examinates the User Agent string given by the browser to detect device information,
 * comparing it to a list of browser and device names it knows about.
 * This technique works, but has drawbacks and may or may not be the right approach,
 * depending on what you're trying to achieve.
 * If you need to detect a specific browser type (e.g. Chrome, Safari, Internet Explorer)
 * or specific category of device (e.g. all iPods), this can do that.
 * @returns selectors (e.g. isModible, isTablet, isDesktop, etc.)
 */
export function useDeviceDetect() {
	return ReactDeviceDetect;
}

/**
 * Return the file specified in event.target.files[0] as a base64-encoded string
 * @param event
 * @returns {Promise<string | undefined>} file data as string
 *
 **/
export const uploadAndReadFile = (
	event: React.ChangeEvent<HTMLInputElement>,
): Promise<string | undefined> => {
	const file = event.target.files?.[0];
	let fileData: string | undefined;
	if (file) {
		const reader = new FileReader();
		return new Promise(resolve => {
			reader.onload = () => {
				const data = reader.result;
				if (typeof data === 'string') {
					try {
						fileData = data;
					} catch (error) {
						console.error('Failed to parse JSON file:', error);
					}
				}
				resolve(fileData);
			};
			reader.readAsText(file);
		});
	}
	return Promise.resolve(fileData);
};

/**
 * Create an anchor element and dispatch a click event on it to trigger a download
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const downloadFile = (data: any, fileName: string, fileType: string) => {
	const blob = new Blob([data], { type: fileType });
	const a = document.createElement('a');
	a.download = fileName;
	a.href = window.URL.createObjectURL(blob);
	const clickEvt = new MouseEvent('click', {
		view: window,
		bubbles: true,
		cancelable: true,
	});
	a.dispatchEvent(clickEvt);
	a.remove();
};

export const sortObjectsByProperties = <T,>(
	objects: T[],
	propertiesInOrderOfRelevance: Array<keyof T & string>,
	sortDirection: 'ASC' | 'DESC' = 'ASC',
) => {
	return objects.sort((objectA, objectB) => {
		let result = 0;
		let propertyIndex = 0;
		while (propertyIndex < propertiesInOrderOfRelevance.length) {
			const property = propertiesInOrderOfRelevance[propertyIndex];
			const propertyValueA = objectA[property];
			const propertyValueB = objectB[property];

			if (sortDirection === 'ASC') {
				if (propertyValueA > propertyValueB) {
					return 1;
				}
				if (propertyValueA < propertyValueB) {
					return -1;
				}
			}
			if (sortDirection === 'DESC') {
				if (propertyValueA < propertyValueB) {
					return 1;
				}
				if (propertyValueA > propertyValueB) {
					return -1;
				}
			}
			if (propertyIndex == propertiesInOrderOfRelevance.length - 1) {
				result = 0;
			} else {
				propertyIndex++;
			}
		}
		return result;
	});
};

/**
 * Handles page change by updating the page filter with the new offset and limit.
 * @param {number} selectedPage - The selected page number.
 * @param {UseQueryResult<Page<T> | undefined, unknown>} getDataQuery - The query result containing the page data.
 * @param {(value: SetStateAction<PageFilter>) => void} setPageFilter - A function to set the page filter state.
 * @template T - The type of the items in the page.
 */
export const handlePageChange = <T,>(
	selectedPage: number,
	getDataQuery: UseQueryResult<Page<T> | undefined, unknown>,
	setPageFilter: (value: SetStateAction<PageFilter>) => void,
) => {
	if (getDataQuery && getDataQuery.data) {
		const limit = getDataQuery.data.metadata.limit;
		const newOffset = (selectedPage - 1) * limit;
		setPageFilter({ offSet: newOffset, limit: limit });
	}
};

// TODO: if WEH API would get renamed as page.metadata.offset or [...].offSet – to harmonize both WEH and DQP APIs – then rename this as Page and remove above
export const handlePageDqpChange = <T,>(
	selectedPage: number,
	getDataQuery: UseQueryResult<PageDqp<T> | undefined, unknown>,
	setPageFilter: (value: SetStateAction<PageDqpFilter>) => void,
) => {
	if (getDataQuery && getDataQuery.data) {
		const limit = getDataQuery.data.metadata.limit;
		const newOffset = (selectedPage - 1) * limit;
		setPageFilter({ offset: newOffset, limit: limit });
	}
};

/**
 * Convert Time to hh:mm string
 * @param value Time
 * @returns string
 */
export const convertTimeToString = (value: Time | null) => {
	if (!value) return '';

	const paddedHour = value.hour < 10 ? `0${value.hour}` : value.hour;
	const paddedMinute = value.minute < 10 ? `0${value.minute}` : value.minute;

	return `${paddedHour}:${paddedMinute}`;
};

/**
 * Convert hh:mm string to Time
 * @param value string
 * @returns Time
 */
export const convertStringToTime = (value: string) => {
	if (!value) return undefined;

	const [hours, minutes] = value.split(':');
	return new Time(parseInt(hours), parseInt(minutes));
};

/**
 *
 * @param minutes Covert minutes to milliseconds
 * @returns milliseconds
 */
export const convertMinutesToMs = (minutes: number): number => minutes * 60 * 1000;

/**
 * Determines the target attribute for a link based on the URL.
 * If the URL belons to the WaterIQ Portal, the link will open in the same tab.
 * Otherwise, it will open in a new tab.
 * @param {string} url - The URL to check.
 * @returns {React.HTMLAttributeAnchorTarget | undefined} - The target attribute for the link.
 */
export const linkTarget = (url?: string): React.HTMLAttributeAnchorTarget | undefined => {
	if (url) {
		const isInternalNavigation =
			url.includes(CONSTANT.PORTAL_URL) || url.includes('localhost') || url.includes('#');
		return isInternalNavigation ? '_self' : '_blank';
	}
};

/**
 * Construct a series of paragraphs and anchors given a content block and set of links with keywords
 * @param {string | undefined} content - pain text content
 * @param {NotificationLinkType[]} links - array of keywords and urls
 * @returns
 */
export const formatContent = (content: string | undefined, links: NotificationLinkType[]) => {
	// identify any email addresses in the notification by regular expression - and add to the list of links
	const emailAddresses = content?.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g);
	emailAddresses?.forEach(e => {
		links.push({ keyword: e, url: 'mailto:' + e });
	});

	// break the content into paragraphs
	const paragraphs: string[] = content ? content.split('\n') : [];

	const paragraphBlocks = [];
	for (let paragraphIndex = 0; paragraphIndex < paragraphs.length; paragraphIndex++) {
		const paragraph = paragraphs[paragraphIndex];
		const contentBlocks = Array<{ content: string; link?: string }>();
		if (links && links.length) {
			let startIndex = 0;
			let lastIndex = 0;

			for (let linkIndex = 0; linkIndex < links.length; linkIndex++) {
				const link = links[linkIndex];

				startIndex = paragraph.indexOf(link.keyword, lastIndex);
				if (startIndex !== -1) {
					// is there a next keyword to create a link for?
					if (startIndex > 0) {
						contentBlocks.push({ content: paragraph.substring(lastIndex, startIndex) });
					}
					contentBlocks.push({ content: link.keyword, link: link.url });
					startIndex += link.keyword.length;
					lastIndex = startIndex;
				}
			}

			// add any remaining text as plain text
			if (lastIndex < paragraph.length) {
				contentBlocks.push({ content: paragraph.substring(lastIndex) });
			}
		} else {
			contentBlocks.push({ content: paragraph });
		}
		paragraphBlocks.push(contentBlocks);
	}
	return paragraphBlocks;
};
